import { ColDef, GetRowIdParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { field3ToFormField } from '../../../data-models/field3.data-model';
import { columnMetaToFieldDefinition } from '../../../data-models/field-definition/column-meta-mapping';
import { formulaFieldToFieldDefinition } from '../../../data-models/field-definition/formula-field-mapping';
import { customFieldToFieldDefinition } from '../../../data-models/field-definition/custom-field-mapping';
import { FieldDefinition } from '../../../data-models/field-definition/field-definition.schema';
import { kpiConfigToFieldDefinition } from '../../../data-models/field-definition/kpi-config-mapping';
import { columnsRawLoadable } from '../../../services/state/AdminPanel/ColumnDefinitionsState';
import { customFieldsListLoadable } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import {
  displayedFormulaFieldIdsLoadable,
  formulaFieldsListLoadable,
} from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { kpiConfigLoadable } from '../../../services/state/KPIConfigState';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { BooleanFormatterId } from '../../../util/formatters/BooleanFormatters';
import { ColumnMeta } from '../../../types';
import { IFormField } from '../../../view-models/form.view-model';
import { getColumnFields } from '../../Config/Columns/ColumnFields';
import { useGetKpiFields, withoutFormatterField } from '../../Config/KPI/KPIFields';
import ActionsCellRenderer from '../CustomFields/components/ActionsCellRenderer';
import useCustomFieldsColDefs from '../CustomFields/hooks/useCustomFieldsColDefs';
import { FormulaFieldEntity, IFormulaFieldDataModel } from '../../../data-models/formula-field.data-model';
import { EditActionCell } from './EditActionCell';
import { FIELD_KIND_LABEL, FieldKind, FieldPill, UnifiedFieldRow } from './fieldManagerModels';
import { FORMULA_ENTITY_LABEL } from './FormulaFieldModal';

type OnEdit = (kind: FieldKind, item: unknown) => void;
type FmRowIdParams = GetRowIdParams<{ id: number; rowKey?: string }>;

export interface FieldManagerGrid {
  columnDefs: ColDef[];
  rowData: unknown[];
  getRowId: (params: FmRowIdParams) => string;
  autoGroupColumnDef?: ColDef;
  /** ag-grid's `-1` for "every level". Only set where the pill groups its own rows. */
  groupDefaultExpanded?: number;
  isLoading: boolean;
  hasError: boolean;
}

function formFieldsToColDefs(fields: IFormField<unknown>[]): ColDef[] {
  return fields.map((field) => ({
    field: field.key,
    headerName: field.label,
    sortable: true,
    valueFormatter: AgFormatterService.get().getFormatterForFormField(field),
  }));
}

function actionCol(onEditRow: (row: unknown) => void): ColDef {
  return {
    colId: '__fmActions',
    headerName: '',
    pinned: 'right',
    flex: 0,
    width: 72,
    minWidth: 72,
    maxWidth: 72,
    sortable: false,
    filter: false,
    resizable: false,
    suppressSizeToFit: true,
    enableRowGroup: false,
    cellRenderer: EditActionCell,
    cellRendererParams: { onEditRow },
  };
}

const COMMON_COLDEFS: ColDef<UnifiedFieldRow>[] = [
  {
    colId: 'kind',
    field: 'kind',
    headerName: 'Type',
    width: 110,
    flex: 0,
    valueFormatter: (p: ValueFormatterParams<UnifiedFieldRow, FieldKind>) =>
      p.value ? FIELD_KIND_LABEL[p.value] : '',
  },
  { field: 'key', headerName: 'Key' },
  { field: 'displayName', headerName: 'Display Name' },
  { field: 'description', headerName: 'Description' },
  { field: 'type', headerName: 'Data Type' },
  { field: 'format', headerName: 'Format' },
  { field: 'sortOrder', headerName: 'Sort Order' },
  { field: 'isActive', headerName: 'Active' },
];

/** The only columns the page's search box matches on. */
const SEARCHABLE_COLUMNS = new Set(['displayName', 'description']);

/**
 * name, description, type, format — the columns every pill leads with. Each
 * pill names them differently (the KPI "Type" is `entity`, the System pill's
 * "Format" is `formatting`), and the Custom pill has no format column at all.
 */
const LEAD_COLUMNS: Record<FieldPill, string[]> = {
  all: ['displayName', 'description', 'kind', 'format'],
  kpi: ['displayName', 'description', 'entity', 'formMeta.renderer.type'],
  custom: ['displayName', 'description', 'formMeta.renderer.type'],
  column: ['displayName', 'description', 'entity', 'type', 'formatting'],
  formula: ['displayName', 'description', 'entity', 'format'],
};

/** Hidden on load, re-addable through the column header menu → Choose Columns. */
const HIDDEN_COLUMNS: Record<FieldPill, string[]> = {
  all: ['key', 'sortOrder', 'isActive'],
  kpi: [],
  custom: [],
  column: [
    'name',
    'path',
    'tableOrder',
    'groupPriority',
    'required',
    'editable',
    'exportable',
    'sortable',
    'options',
  ],
  formula: ['key'],
};

const colKey = (def: ColDef) => def.colId ?? def.field;

/**
 * Applies the pill's default layout to whatever colDefs its branch produced:
 * hides the columns that load collapsed, scopes the quick filter to name and
 * description, and leads with `LEAD_COLUMNS`. Keyed on `colId ?? field` so one
 * pass covers all four colDef sources (inline defs, `formFieldsToColDefs`,
 * `useCustomFieldsColDefs`). The action column has no `field`, so it falls to
 * the tail and stays last.
 */
function applyPillLayout(pill: FieldPill, defs: ColDef[]): ColDef[] {
  const hidden = new Set(HIDDEN_COLUMNS[pill]);
  const normalized = defs.map((def) => {
    const key = colKey(def);
    return {
      ...def,
      ...(key && hidden.has(key) ? { hide: true } : {}),
      ...(key && SEARCHABLE_COLUMNS.has(key) ? {} : { getQuickFilterText: () => '' }),
    };
  });

  const lead = LEAD_COLUMNS[pill];
  const byKey = new Map(normalized.map((def) => [colKey(def), def]));
  const leading = lead.map((key) => byKey.get(key)).filter((def): def is ColDef => def != null);
  const leadKeys = new Set(leading.map(colKey));
  return [...leading, ...normalized.filter((def) => !leadKeys.has(colKey(def)))];
}

/**
 * A system field's entity, derived rather than stored: the company vocabulary
 * describes Company.schema fields under a "company." prefix and every other
 * path as a field of the metrics row, so the prefix is what separates them.
 */
export function columnEntityLabel(path: string | undefined): string {
  return path?.startsWith('company.') ? 'Company' : 'CompanyMetric';
}

function unifyRow(kind: FieldKind, source: { id: number }, fd: FieldDefinition): UnifiedFieldRow {
  return { ...fd, rowKey: `${kind}:${source.id}`, kind, id: source.id, source };
}

/**
 * Builds the ag-grid `columnDefs` + `rowData` for the selected pill. Specific
 * pills show that type's native fields (reusing each type's existing field/colDef
 * definitions); the "All" pill shows the shared `FieldDefinition` columns across
 * every type. Every pill gets an edit-only action column.
 */
export function useFieldManagerGrid(pill: FieldPill, onEdit: OnEdit): FieldManagerGrid {
  const kpiLoad = useAtomValue(kpiConfigLoadable);
  const customLoad = useAtomValue(customFieldsListLoadable);
  const columnLoad = useAtomValue(columnsRawLoadable);
  const formulaLoad = useAtomValue(formulaFieldsListLoadable);
  const displayedLoad = useAtomValue(displayedFormulaFieldIdsLoadable);
  const kpiFields = useGetKpiFields();
  const { columnDefs: customColDefs } = useCustomFieldsColDefs();

  // displayedLoad belongs here too: the Displayed column reads it, and without
  // it every formula row reads "No" until the registrations resolve.
  const isLoading =
    kpiLoad.state === 'loading' ||
    customLoad.state === 'loading' ||
    columnLoad.state === 'loading' ||
    formulaLoad.state === 'loading' ||
    displayedLoad.state === 'loading';
  const hasError =
    kpiLoad.state === 'hasError' ||
    customLoad.state === 'hasError' ||
    columnLoad.state === 'hasError' ||
    formulaLoad.state === 'hasError' ||
    displayedLoad.state === 'hasError';

  const grid = useMemo<
    Pick<
      FieldManagerGrid,
      'columnDefs' | 'rowData' | 'getRowId' | 'autoGroupColumnDef' | 'groupDefaultExpanded'
    >
  >(() => {
    const kpi = kpiLoad.state === 'hasData' ? (kpiLoad.data ?? []) : [];
    const custom = customLoad.state === 'hasData' ? (customLoad.data ?? []) : [];
    const columns = columnLoad.state === 'hasData' ? (columnLoad.data ?? []) : [];
    const formula = formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [];
    const displayedIds =
      displayedLoad.state === 'hasData' ? (displayedLoad.data ?? new Set<number>()) : new Set<number>();

    switch (pill) {
      case 'kpi':
        return {
          columnDefs: applyPillLayout(pill, [
            ...formFieldsToColDefs(withoutFormatterField(kpiFields).map((f) => field3ToFormField(f))),
            actionCol((row) => onEdit('kpi', row)),
          ]),
          rowData: kpi as unknown[],
          getRowId: (p) => `kpi:${p.data.id}`,
        };
      case 'custom':
        return {
          columnDefs: applyPillLayout(pill, [
            ...(customColDefs as ColDef[]).filter((def) => def.cellRenderer !== ActionsCellRenderer),
            actionCol((row) => onEdit('custom', row)),
          ]),
          rowData: custom as unknown[],
          getRowId: (p) => `custom:${p.data.id}`,
          autoGroupColumnDef: { minWidth: 220 },
          // The entity groups are the shape of the list, not a filter to open one
          // at a time — collapsed, the pill loads showing five rows and no fields.
          groupDefaultExpanded: -1,
        };
      case 'column':
        return {
          columnDefs: applyPillLayout(pill, [
            ...formFieldsToColDefs(getColumnFields()),
            {
              colId: 'entity',
              headerName: 'Entity',
              sortable: true,
              valueGetter: (p: ValueGetterParams<ColumnMeta>) => columnEntityLabel(p.data?.path),
            },
            actionCol((row) => onEdit('column', row)),
          ]),
          rowData: columns as unknown[],
          getRowId: (p) => `column:${p.data.id}`,
        };
      case 'formula':
        return {
          columnDefs: applyPillLayout(pill, [
            { field: 'key', headerName: 'Key', sortable: true },
            { field: 'displayName', headerName: 'Display Name', sortable: true },
            {
              field: 'entity',
              headerName: 'Entity',
              sortable: true,
              valueFormatter: (p: ValueFormatterParams) =>
                FORMULA_ENTITY_LABEL[p.value as FormulaFieldEntity] ?? String(p.value ?? ''),
            },
            { field: 'description', headerName: 'Description', sortable: true },
            { field: 'format', headerName: 'Format', sortable: true },
            {
              // Current display state, so a bulk edit can be targeted. Read
              // from the registrations rather than the row: display config
              // lives in fieldDisplayConfig, not on the field.
              colId: 'displayed',
              headerName: 'Displayed',
              sortable: true,
              width: 120,
              flex: 0,
              valueGetter: (p: ValueGetterParams<IFormulaFieldDataModel>) =>
                p.data ? displayedIds.has(p.data.id) : null,
              valueFormatter: AgFormatterService.get().getFormatterForId(BooleanFormatterId.yesNo),
            },
            { field: 'formula', headerName: 'Formula', sortable: false, flex: 2 },
            actionCol((row) => onEdit('formula', row)),
          ]),
          rowData: formula as unknown[],
          getRowId: (p) => `formula:${p.data.id}`,
        };
      case 'all':
      default: {
        const rows: UnifiedFieldRow[] = [
          ...kpi.map((k) => unifyRow('kpi', k, kpiConfigToFieldDefinition(k))),
          ...custom.map((c) => unifyRow('custom', c, customFieldToFieldDefinition(c))),
          ...columns.map((c) => unifyRow('column', c, columnMetaToFieldDefinition(c))),
          ...formula.map((f) => unifyRow('formula', f, formulaFieldToFieldDefinition(f))),
        ];
        return {
          columnDefs: applyPillLayout(pill, [
            ...(COMMON_COLDEFS as ColDef[]),
            actionCol((row) => onEdit((row as UnifiedFieldRow).kind, (row as UnifiedFieldRow).source)),
          ]),
          rowData: rows as unknown[],
          getRowId: (p) => p.data.rowKey ?? `unknown:${p.data.id}`,
        };
      }
    }
  }, [pill, kpiLoad, customLoad, columnLoad, formulaLoad, displayedLoad, kpiFields, customColDefs, onEdit]);

  return { ...grid, isLoading, hasError };
}
